import { Box } from "@mui/material";
import type { ReactElement } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslate } from "react-admin";
import type { LabelProps } from "recharts";
import {
  Bar,
  type BarShapeProps,
  CartesianGrid,
  Cell,
  ComposedChart,
  Dot,
  LabelList,
  Line,
  Rectangle,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

import { formatAxisTick, SALESFORCE_CHART_COLORS, toNumberOrNull } from "../lib/salesforceDashboard";
import {
  buildSideBySideBars,
  fitSideBySideBarWidth,
  groupKeyValue,
  type ReportBars,
  type ReportSeries,
  type SideBySideBar,
  type SideBySideBars,
  slantedTickHeight,
  truncateLabel,
  truncateTickLabel,
  valueAxisTicks,
} from "../lib/salesforceReport";
import { space } from "../theme/designTokens";
import {
  axisTickStyle,
  CLICKABLE_STYLE,
  HorizontalAxisTitle,
  hoveredBarSx,
  percentFormat,
  SalesforceEmptyState,
  type SalesforceLegendEntry,
  SalesforceVerticalLegend,
  VerticalAxisTitle,
  visibleScrollbarSx,
  Y_AXIS_WIDTH_PX,
} from "./SalesforceDashboardCharts";
import { SalesforceHoverTooltip, useChartHover } from "./SalesforceHoverTooltip";

const colors = SALESFORCE_CHART_COLORS;

const VALUE_AXIS_ID = "value";
const LINE_AXIS_ID = "line";
const MAX_BAR_SIZE_PX = 80;
const ANGLED_TICK_MIN_ROWS = 4;
const FLAT_TICK_HEIGHT_PX = 24;
const CATEGORY_AXIS_WIDTH_PX = 140;
// Salesforce gives a report's legend about two fifths of the chart area.
const LEGEND_WIDTH = "40%";
// Side-by-side columns give the plot more room: the legend holds only short names, cut at their end when long.
const SIDE_BY_SIDE_LEGEND_WIDTH = "34%";
const LABEL_TEXT_GAP_PX = 4;
const LINE_DOT_RADIUS_PX = 4;
// Side-by-side columns fill the plot up to a maximum width; when they do not fit at the minimum width, the plot scrolls.
const SIDE_BY_SIDE_MIN_BAR_WIDTH_PX = 16;
const SIDE_BY_SIDE_MAX_BAR_WIDTH_PX = 30;
const SIDE_BY_SIDE_BAR_GAP_PX = 2;
const SIDE_BY_SIDE_GROUP_GAP_PX = 10;
// Both label rows are cut to a few characters ("Mar…", "13.…") so the bars keep most of the height.
const SERIES_LABEL_ROW_HEIGHT_PX = 34;
const GROUP_LABEL_ROW_HEIGHT_PX = 32;
const LABEL_ROWS_GAP_PX = 4;
const MAX_SERIES_LABEL_CHARS = 4;
const MAX_GROUP_LABEL_CHARS = 4;
const PLOT_TOP_MARGIN_PX = 16;
// Room under the baseline, so the axis's zero is not cut in half.
const PLOT_BOTTOM_MARGIN_PX = 6;
const SIDE_BY_SIDE_AXIS_WIDTH_PX = 30;
const VALUE_LABEL_CHAR_WIDTH_PX = 6.5;
const VALUE_LABEL_INSET_PX = 4;
const VALUE_LABEL_FONT_SIZE_PX = 12;
const VALUE_LABEL_SIDE_PADDING_PX = 2;

const wholePercentFormat = new Intl.NumberFormat("de-DE", { style: "percent", maximumFractionDigits: 0 });

export type ReportBarMode = "grouped" | "stacked" | "percent";

type ReportLine = {
  label: string;
  color: string;
  formatValue: (value: number) => string;
};

type SalesforceReportBarChartProps = {
  showValues?: boolean;
  bars: ReportBars;
  // Columns stand upright over the groups; bars lie across them.
  orientation: "column" | "bar";
  mode: ReportBarMode;
  // The axis title, which joins both groupings.
  categoryTitle: string;
  // The first grouping's own label, for the tooltip.
  firstGroupingTitle: string;
  valueTitle: string;
  // Shown when the series are a dimension or the measures; a single series needs no legend.
  legendTitle?: string;
  // The series are the chart's measures, so each one names its own tooltip value.
  seriesAreMeasures?: boolean;
  formatValue: (value: number, seriesIndex: number) => string;
  line?: ReportLine;
  // A click on a bar or segment gives the group's value and, when the series are a dimension, the series' value.
  onSelect?: (values: Array<string | null>) => void;
};

type ChartRow = Record<string, string | number | null>;

const seriesDataKey = (index: number): string => `series-${index}`;
const LINE_DATA_KEY = "line";

const rowSum = (values: Array<number | null>): number => values.reduce<number>((sum, value) => sum + (value ?? 0), 0);

const categoryTickLayout = (labels: string[]) =>
  labels.length > ANGLED_TICK_MIN_ROWS
    ? ({ angle: -45, textAnchor: "end", height: slantedTickHeight(labels), slanted: true } as const)
    : ({ angle: 0, textAnchor: "middle", height: FLAT_TICK_HEIGHT_PX, slanted: false } as const);

// Slanted labels are cut short; the tooltips keep the full label.
const slantedTickFormatter = (value: unknown): string => truncateTickLabel(String(value));

type SideBySideDatum = { key: string; value: number };

const groupWidth = (barCount: number, barWidth: number): number =>
  barCount * barWidth + (barCount - 1) * SIDE_BY_SIDE_BAR_GAP_PX + SIDE_BY_SIDE_GROUP_GAP_PX;

// Where each bar starts across the plot, and how wide the plot is: every group has half a group gap on each side.
const sideBySideLayout = ({ groups }: SideBySideBars, barWidth: number) => {
  const lefts: number[] = [];
  let width = 0;
  groups.forEach(group => {
    for (let index = 0; index < group.barCount; index += 1) {
      lefts.push(width + SIDE_BY_SIDE_GROUP_GAP_PX / 2 + index * (barWidth + SIDE_BY_SIDE_BAR_GAP_PX));
    }
    width += groupWidth(group.barCount, barWidth);
  });
  return { lefts, width };
};

// The width the element has, kept up to date; zero until it is measured.
const useElementWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const update = () => setWidth(element.clientWidth);
    update();
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(update);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, []);

  return { ref, width };
};

// Text read from the bottom up, ending against the plot.
const RotatedLabel = ({
  label,
  shownLabel = label,
  maxChars,
  height,
  width,
}: {
  label: string;
  // What is drawn when it is not the full label; the full label stays in the title.
  shownLabel?: string;
  maxChars: number;
  height: number;
  width: number;
}) => (
  <Box
    sx={{
      display: "flex",
      flex: "none",
      justifyContent: "center",
      width: `${width}px`,
      height: `${height}px`,
      overflow: "hidden",
    }}
    title={label}
  >
    <Box
      component="span"
      sx={{
        fontSize: "12px",
        lineHeight: "16px",
        textAlign: "end",
        whiteSpace: "nowrap",
        color: colors.text,
        transform: "rotate(180deg)",
        writingMode: "vertical-rl",
      }}
    >
      {truncateLabel(shownLabel, maxChars)}
    </Box>
  </Box>
);

type SideBySideColumnsProps = {
  showValues: boolean;
  layout: SideBySideBars;
  categoryTitle: string;
  valueTitle: string;
  formatValue: (value: number, seriesIndex: number) => string;
  onBarEnter: (bar: SideBySideBar, event: React.MouseEvent) => void;
  onBarLeave: () => void;
  onBarClick?: (bar: SideBySideBar) => void;
};

const SideBySideColumns = ({
  showValues,
  layout,
  categoryTitle,
  valueTitle,
  formatValue,
  onBarEnter,
  onBarLeave,
  onBarClick,
}: SideBySideColumnsProps) => {
  const translate = useTranslate();
  const { bars, groups } = layout;
  const { ref, width: viewportWidth } = useElementWidth();
  // The scroll region is named after the heading of the card it sits in.
  const [cardTitle, setCardTitle] = useState<string>();
  useEffect(() => {
    const titleId = ref.current?.closest("section")?.getAttribute("aria-labelledby");
    setCardTitle((titleId && document.getElementById(titleId)?.textContent) || undefined);
  }, [ref]);
  const barWidth = Math.floor(
    fitSideBySideBarWidth({
      available: viewportWidth - SIDE_BY_SIDE_AXIS_WIDTH_PX,
      barCount: bars.length,
      groupCount: groups.length,
      barGap: SIDE_BY_SIDE_BAR_GAP_PX,
      groupGap: SIDE_BY_SIDE_GROUP_GAP_PX,
      minWidth: SIDE_BY_SIDE_MIN_BAR_WIDTH_PX,
      maxWidth: SIDE_BY_SIDE_MAX_BAR_WIDTH_PX,
    })
  );
  const { lefts, width } = useMemo(() => sideBySideLayout(layout, barWidth), [layout, barWidth]);
  const data = useMemo<SideBySideDatum[]>(() => bars.map(({ key, value }) => ({ key, value })), [bars]);
  const ticks = useMemo(() => valueAxisTicks(bars.map(bar => bar.value)), [bars]);
  // Both charts take the same scale, so the fixed axis labels the scrolled plot.
  const domain: [number, number] = [ticks[0], ticks[ticks.length - 1]];
  const chartMargin = { top: PLOT_TOP_MARGIN_PX, right: 0, bottom: PLOT_BOTTOM_MARGIN_PX, left: 0 };

  // One element while the pointer moves: Recharts replaces the bars' nodes when a Bar element is new, and a click
  // whose mousedown and mouseup land on different nodes never fires.
  const plotBar = useMemo(() => {
    // The value reads across the bar at its top when it fits the bar's width, else upwards; just above a bar too short to hold it.
    const renderBar = (props: BarShapeProps): ReactElement => {
      const bar = bars[props.index];
      const left = lefts[props.index] ?? props.x;
      const top = Math.min(props.y, props.y + props.height);
      const text = bar ? formatValue(bar.value, bar.seriesIndex) : "";
      const textWidth = text.length * VALUE_LABEL_CHAR_WIDTH_PX;
      const horizontal = textWidth + 2 * VALUE_LABEL_SIDE_PADDING_PX <= barWidth;
      const inside =
        Math.abs(props.height) >= (horizontal ? VALUE_LABEL_FONT_SIZE_PX : textWidth) + 2 * VALUE_LABEL_INSET_PX;
      const labelX = left + barWidth / 2;
      const labelY = horizontal
        ? top +
          (inside
            ? VALUE_LABEL_INSET_PX + VALUE_LABEL_FONT_SIZE_PX / 2
            : -VALUE_LABEL_INSET_PX - VALUE_LABEL_FONT_SIZE_PX / 2)
        : inside
          ? top + VALUE_LABEL_INSET_PX
          : top - VALUE_LABEL_INSET_PX;
      return (
        <g style={onBarClick ? CLICKABLE_STYLE : undefined}>
          <Rectangle {...props} width={barWidth} x={left} />
          {showValues && (
            <text
              dominantBaseline="central"
              fill={colors.textStrong}
              fontSize={VALUE_LABEL_FONT_SIZE_PX}
              pointerEvents="none"
              textAnchor={horizontal ? "middle" : inside ? "end" : "start"}
              transform={horizontal ? undefined : `rotate(-90 ${labelX} ${labelY})`}
              x={labelX}
              y={labelY}
            >
              {text}
            </text>
          )}
        </g>
      );
    };

    const enter = (payload: unknown, event: React.MouseEvent) => {
      const key = (payload as Partial<SideBySideDatum> | undefined)?.key;
      const bar = bars.find(candidate => candidate.key === key);
      if (bar) onBarEnter(bar, event);
    };

    const click = (payload: unknown) => {
      const key = (payload as Partial<SideBySideDatum> | undefined)?.key;
      const bar = bars.find(candidate => candidate.key === key);
      if (bar) onBarClick?.(bar);
    };

    return (
      <Bar
        dataKey="value"
        isAnimationActive={false}
        onMouseEnter={(bar, _, event) => enter(bar.payload, event)}
        onMouseLeave={onBarLeave}
        onClick={onBarClick ? bar => click(bar.payload) : undefined}
        onMouseMove={(bar, _, event) => enter(bar.payload, event)}
        shape={renderBar}
        style={onBarClick ? CLICKABLE_STYLE : undefined}
      >
        {bars.map(bar => (
          <Cell fill={bar.color} key={bar.key} strokeWidth={0} />
        ))}
      </Bar>
    );
  }, [bars, lefts, barWidth, formatValue, onBarEnter, onBarLeave, onBarClick, showValues]);

  // Browsers scroll Home and End vertically only; here they jump to the first and last week.
  const scrollToEdge = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    event.currentTarget.scrollLeft = event.key === "Home" ? 0 : event.currentTarget.scrollWidth;
  };

  return (
    <Box
      aria-label={translate("salesforceReports.scrollRegion", { title: cardTitle ?? valueTitle })}
      onKeyDown={scrollToEdge}
      ref={ref}
      role="region"
      sx={{
        ...visibleScrollbarSx,
        display: "flex",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflowX: "auto",
        overflowY: "hidden",
      }}
      tabIndex={0}
    >
      <Box
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 1,
          display: "flex",
          flex: "none",
          flexDirection: "column",
          width: `${SIDE_BY_SIDE_AXIS_WIDTH_PX}px`,
          backgroundColor: colors.cardBackground,
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer height="100%" width={SIDE_BY_SIDE_AXIS_WIDTH_PX}>
            {/* Only draws the fixed value axis; the plot beside it carries the chart for assistive technology. */}
            <ComposedChart accessibilityLayer={false} aria-hidden data={data} margin={chartMargin} tabIndex={-1}>
              <XAxis dataKey="key" hide />
              {/* Recharts draws a value axis's ticks only for a chart with a bar; this one is not seen. */}
              <Bar dataKey="value" fill="transparent" isAnimationActive={false} />
              <YAxis
                axisLine={false}
                domain={domain}
                interval={0}
                tick={axisTickStyle(12, colors.tick)}
                tickFormatter={formatAxisTick}
                tickLine={false}
                ticks={ticks}
                width={SIDE_BY_SIDE_AXIS_WIDTH_PX}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        <Box
          sx={{
            flex: "none",
            height: `${SERIES_LABEL_ROW_HEIGHT_PX + LABEL_ROWS_GAP_PX + GROUP_LABEL_ROW_HEIGHT_PX}px`,
          }}
        />
      </Box>
      <Box sx={{ display: "flex", flex: "none", flexDirection: "column", width: `${width}px` }}>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer height="100%" width={width}>
            <ComposedChart
              aria-label={translate("salesforceReports.sideBySideChart", {
                value: valueTitle,
                category: categoryTitle,
              })}
              data={data}
              margin={chartMargin}
            >
              <CartesianGrid horizontal stroke={colors.grid} vertical={false} />
              <XAxis dataKey="key" hide />
              <YAxis domain={domain} hide interval={0} ticks={ticks} />
              {plotBar}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ display: "flex", flex: "none" }}>
          {groups.map((group, groupIndex) => (
            <Box
              key={group.key}
              sx={{
                position: "relative",
                display: "flex",
                flex: "none",
                flexDirection: "column",
                boxSizing: "border-box",
                width: `${groupWidth(group.barCount, barWidth)}px`,
                px: `${SIDE_BY_SIDE_GROUP_GAP_PX / 2}px`,
                // A thin separator sits in the gap between this group and the one before it.
                "&::before":
                  groupIndex > 0
                    ? {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        width: "1px",
                        backgroundColor: colors.grid,
                      }
                    : undefined,
              }}
            >
              <Box sx={{ display: "flex", gap: `${SIDE_BY_SIDE_BAR_GAP_PX}px` }}>
                {bars.slice(group.firstBar, group.firstBar + group.barCount).map(bar => (
                  <RotatedLabel
                    height={SERIES_LABEL_ROW_HEIGHT_PX}
                    key={bar.key}
                    label={bar.seriesLabel}
                    maxChars={MAX_SERIES_LABEL_CHARS}
                    width={barWidth}
                  />
                ))}
              </Box>
              <Box sx={{ mt: `${LABEL_ROWS_GAP_PX}px` }}>
                <RotatedLabel
                  height={GROUP_LABEL_ROW_HEIGHT_PX}
                  label={group.label}
                  maxChars={MAX_GROUP_LABEL_CHARS}
                  shownLabel={bars[group.firstBar]?.groupShortLabel}
                  width={groupWidth(group.barCount, barWidth) - SIDE_BY_SIDE_GROUP_GAP_PX}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

type LineDotProps = { cx?: number; cy?: number; payload?: ChartRow };

export const SalesforceReportBarChart = ({
  showValues = true,
  bars,
  orientation,
  mode,
  categoryTitle,
  firstGroupingTitle,
  valueTitle,
  legendTitle,
  seriesAreMeasures = false,
  formatValue,
  line,
  onSelect,
}: SalesforceReportBarChartProps) => {
  const translate = useTranslate();
  const { hover, show, hide } = useChartHover();
  const isColumn = orientation === "column";

  const data = useMemo<ChartRow[]>(
    () =>
      bars.rows.map(row => ({
        key: row.key,
        label: row.label,
        [LINE_DATA_KEY]: row.lineValue,
        ...Object.fromEntries(bars.series.map((_, index) => [seriesDataKey(index), row.values[index] ?? null])),
      })),
    [bars]
  );

  // Salesforce draws a column chart grouped by a second dimension as bars that exist, side by side.
  const isSideBySide = isColumn && mode === "grouped" && !seriesAreMeasures && legendTitle !== undefined;
  const sideBySide = useMemo(() => (isSideBySide ? buildSideBySideBars(bars) : null), [bars, isSideBySide]);

  const legendEntries = useMemo<SalesforceLegendEntry[]>(
    () => [
      ...bars.series.map(({ key, label, color }) => ({ key, label, color })),
      ...(line ? [{ key: LINE_DATA_KEY, label: line.label, color: line.color }] : []),
    ],
    [bars.series, line]
  );

  const showBar = useCallback(
    (payload: unknown, series: ReportSeries, seriesIndex: number, event: React.MouseEvent) => {
      const rowKey = (payload as Partial<ChartRow> | undefined)?.key;
      const row = bars.rows.find(candidate => candidate.key === rowKey);
      const value = row?.values[seriesIndex] ?? null;
      if (!row || value === null) return;
      const total = rowSum(row.values);
      const formatted = formatValue(value, seriesIndex);
      show(event, {
        rows: [
          { label: firstGroupingTitle, value: row.label },
          ...(legendTitle && !seriesAreMeasures
            ? [{ label: legendTitle, value: series.label, color: series.color }]
            : []),
        ],
        metric: seriesAreMeasures
          ? { label: series.label, value: formatted, color: series.color }
          : { label: valueTitle, value: formatted, color: legendTitle ? undefined : series.color },
        share:
          mode !== "grouped" && total > 0
            ? translate("salesforceDashboard.tooltip.shareOfPeriod", {
                percent: percentFormat.format(value / total),
                total: formatValue(total, seriesIndex),
                period: row.label,
              })
            : undefined,
      });
    },
    [bars.rows, firstGroupingTitle, formatValue, legendTitle, mode, seriesAreMeasures, show, translate, valueTitle]
  );

  const showLinePoint = useCallback(
    (payload: ChartRow | undefined, event: React.MouseEvent) => {
      const value = toNumberOrNull(payload?.[LINE_DATA_KEY]);
      if (!line || value === null) return;
      show(event, {
        rows: [{ label: firstGroupingTitle, value: String(payload?.label ?? "") }],
        metric: { label: line.label, value: line.formatValue(value), color: line.color },
      });
    },
    [firstGroupingTitle, line, show]
  );

  const clear = hide;

  const selectsSeries = legendTitle !== undefined && !seriesAreMeasures;
  const select = useMemo(
    () =>
      onSelect
        ? (rowKey: string, series: ReportSeries) =>
            onSelect(selectsSeries ? [groupKeyValue(rowKey), groupKeyValue(series.key)] : [groupKeyValue(rowKey)])
        : undefined,
    [onSelect, selectsSeries]
  );

  const renderEndLabel = useCallback(
    (props: LabelProps): ReactElement | null => {
      const value = toNumberOrNull(props.value);
      const x = Number(props.x);
      const y = Number(props.y);
      const width = Number(props.width);
      const height = Number(props.height);
      if (value === null || ![x, y, width, height].every(Number.isFinite)) return null;
      return (
        <text
          dominantBaseline="central"
          fill={colors.textStrong}
          fontSize={12}
          x={x + width + LABEL_TEXT_GAP_PX}
          y={y + height / 2}
        >
          {formatValue(value, 0)}
        </text>
      );
    },
    [formatValue]
  );

  const valueTickFormatter = useCallback(
    (value: number): string => (mode === "percent" ? wholePercentFormat.format(value) : formatAxisTick(value)),
    [mode]
  );

  // The bars stay one set of elements while the pointer moves: Recharts replaces their nodes when a Bar element is
  // new, and a click whose mousedown and mouseup land on different nodes never fires. The hover look is CSS.
  const barElements = useMemo(() => {
    if (sideBySide) return null;
    const valueAxisProps = isColumn ? { yAxisId: VALUE_AXIS_ID } : {};
    const stacked = mode !== "grouped";
    const entries = bars.series.map((series, index) => ({ series, index }));
    // The first series is drawn on top of a stack, as the legend lists it.
    const renderOrder = stacked ? [...entries].reverse() : entries;
    // The side-by-side columns draw their own bars.
    return renderOrder.map(({ series, index }) => (
      <Bar
        {...valueAxisProps}
        dataKey={seriesDataKey(index)}
        fill={series.color}
        isAnimationActive={false}
        key={series.key}
        maxBarSize={MAX_BAR_SIZE_PX}
        onMouseEnter={(bar, _, event) => showBar(bar.payload, series, index, event)}
        onMouseLeave={clear}
        onClick={
          select
            ? bar => {
                const rowKey = (bar.payload as Partial<ChartRow> | undefined)?.key;
                if (typeof rowKey === "string") select(rowKey, series);
              }
            : undefined
        }
        onMouseMove={(bar, _, event) => showBar(bar.payload, series, index, event)}
        stackId={stacked ? "groups" : undefined}
        style={select ? CLICKABLE_STYLE : undefined}
      >
        {bars.rows.map(row => (
          <Cell fill={series.color} key={row.key} strokeWidth={0} />
        ))}
        {showValues && !isColumn && bars.series.length === 1 && (
          <LabelList content={renderEndLabel} dataKey={seriesDataKey(index)} />
        )}
      </Bar>
    ));
  }, [bars, isColumn, mode, sideBySide, showBar, clear, select, renderEndLabel, showValues]);

  const selectSideBySide = useMemo(
    () => (select ? (bar: SideBySideBar) => select(bar.groupKey, bars.series[bar.seriesIndex]) : undefined),
    [select, bars.series]
  );
  const enterSideBySide = useCallback(
    (bar: SideBySideBar, event: React.MouseEvent) =>
      showBar({ key: bar.groupKey }, bars.series[bar.seriesIndex], bar.seriesIndex, event),
    [showBar, bars.series]
  );

  if (bars.rows.length === 0 || sideBySide?.bars.length === 0) return <SalesforceEmptyState />;

  const tickLayout = categoryTickLayout(bars.rows.map(row => row.label));

  return (
    <Box sx={{ display: "flex", flex: 1, gap: `${space.md}px`, minWidth: 0, minHeight: 0, ...hoveredBarSx }}>
      <VerticalAxisTitle>{isColumn ? valueTitle : categoryTitle}</VerticalAxisTitle>
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0 }}>
        {sideBySide ? (
          <SideBySideColumns
            showValues={showValues}
            formatValue={formatValue}
            categoryTitle={categoryTitle}
            layout={sideBySide}
            onBarEnter={enterSideBySide}
            onBarClick={selectSideBySide}
            onBarLeave={clear}
            valueTitle={valueTitle}
          />
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer height="100%" width="100%">
              <ComposedChart
                barCategoryGap="20%"
                data={data}
                layout={isColumn ? "horizontal" : "vertical"}
                margin={{ top: 16, right: isColumn ? 8 : 40, bottom: 0, left: 0 }}
                stackOffset={mode === "percent" ? "expand" : "none"}
              >
                <CartesianGrid stroke={colors.grid} horizontal={isColumn} vertical={!isColumn} />
                {isColumn ? (
                  <>
                    <XAxis
                      angle={tickLayout.angle}
                      axisLine={false}
                      dataKey="label"
                      height={tickLayout.height}
                      interval="preserveStart"
                      tick={axisTickStyle(12, colors.text)}
                      textAnchor={tickLayout.textAnchor}
                      tickFormatter={tickLayout.slanted ? slantedTickFormatter : undefined}
                      tickLine={false}
                    />
                    <YAxis
                      axisLine={false}
                      tick={axisTickStyle(12, colors.tick)}
                      tickFormatter={valueTickFormatter}
                      tickLine={false}
                      width={Y_AXIS_WIDTH_PX}
                      yAxisId={VALUE_AXIS_ID}
                    />
                  </>
                ) : (
                  <>
                    <XAxis
                      axisLine={false}
                      tick={axisTickStyle(12, colors.tick)}
                      tickFormatter={valueTickFormatter}
                      tickLine={false}
                      type="number"
                    />
                    <YAxis
                      axisLine={false}
                      dataKey="label"
                      interval={0}
                      tick={axisTickStyle(12, colors.text)}
                      tickLine={false}
                      type="category"
                      width={CATEGORY_AXIS_WIDTH_PX}
                    />
                  </>
                )}
                {line && (
                  <YAxis
                    axisLine={false}
                    orientation="right"
                    tick={axisTickStyle(12, line.color)}
                    tickFormatter={formatAxisTick}
                    tickLine={false}
                    width={Y_AXIS_WIDTH_PX}
                    yAxisId={LINE_AXIS_ID}
                  />
                )}
                {barElements}
                {line && (
                  <Line
                    dataKey={LINE_DATA_KEY}
                    dot={(props: LineDotProps) => (
                      <Dot
                        cx={props.cx}
                        cy={props.cy}
                        fill={line.color}
                        key={String(props.payload?.key)}
                        onMouseEnter={(_, event) => showLinePoint(props.payload, event)}
                        onMouseLeave={clear}
                        onMouseMove={(_, event) => showLinePoint(props.payload, event)}
                        r={LINE_DOT_RADIUS_PX}
                      />
                    )}
                    isAnimationActive={false}
                    stroke={line.color}
                    strokeWidth={2}
                    type="linear"
                    yAxisId={LINE_AXIS_ID}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        )}
        <HorizontalAxisTitle>{isColumn ? categoryTitle : valueTitle}</HorizontalAxisTitle>
      </Box>
      {legendTitle && (
        <SalesforceVerticalLegend
          entries={legendEntries}
          maxWidth="none"
          title={legendTitle}
          width={sideBySide ? SIDE_BY_SIDE_LEGEND_WIDTH : LEGEND_WIDTH}
          // Measure names are long and alike, so they wrap; a dimension's values stay on one line.
          wrapLabels={seriesAreMeasures}
        />
      )}
      <SalesforceHoverTooltip hover={hover} />
    </Box>
  );
};
